import { useCallback, useRef, useMemo } from 'react'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import ImpressionsBuffer from 'Utils/ImpressionsBuffer'

import useItemsPerRow from './useItemsPerRow'

function parseToInteger(value) {
  const parsedValue = parseInt(value, 10)
  return Number.isNaN(parsedValue) ? undefined : parsedValue
}

function resolveProductPricing({ cart, attributes, isCurrentUser }) {
  const { price: originalPrice, saleOffDiscount, isSaleOffApplicable, isCLDApplicable } = attributes
  const { isSaleOff, isCLDDisabled, value, applicableCldValue, creditsAvailable } = cart

  const calculateDiscount = (discountedPrice) => ({
    price: discountedPrice,
    originalPrice,
    discount: 1.0 - discountedPrice / originalPrice,
    discountAmount: originalPrice - discountedPrice,
  })

  if (isSaleOff && isSaleOffApplicable && saleOffDiscount !== 0) {
    const discountedPrice = (originalPrice * (100 - saleOffDiscount)) / 100.0
    return { ...calculateDiscount(discountedPrice), ribbon: 'saleOff' }
  }

  if (!isCLDDisabled && isCLDApplicable) {
    const isCartSizeSufficient = value + originalPrice >= applicableCldValue

    if (isCurrentUser && isCartSizeSufficient) {
      const discountedPrice = Math.max(originalPrice - creditsAvailable, originalPrice * 0.7)
      return { ...calculateDiscount(discountedPrice), ribbon: 'CLD' }
    }

    return {
      price: originalPrice,
      originalPrice,
      discount: 0,
      discountAmount: 0,
      ribbon: 'CLD',
    }
  }

  return {
    price: originalPrice,
    originalPrice,
    discount: 0,
  }
}

function prepareItemData(itemAttributes, pageConfig, index, itemListId, itemListName) {
  if (!pageConfig?.cart) {
    return null
  }

  const productPricing = resolveProductPricing({
    cart: pageConfig.cart,
    attributes: itemAttributes,
    isCurrentUser: pageConfig.isCurrentUser,
  })

  let discount = 0
  let price

  if (itemAttributes.searchingFree && productPricing.price > 0) {
    price = itemAttributes.price.toFixed(2)
  } else if (['saleOff', 'CLD'].includes(productPricing.ribbon) && productPricing.discount > 0) {
    discount = (productPricing.originalPrice - productPricing.price).toFixed(2)
    price = productPricing.price.toFixed(2)
  } else {
    price = itemAttributes.price.toFixed(2)
  }

  return {
    item_id: String(itemAttributes.id),
    item_name: itemAttributes.title,
    currency: 'USD',
    index: parseToInteger(index),
    item_brand: itemAttributes.authorUsername,
    item_category: itemAttributes.category,
    item_category2: itemAttributes.subcategory,
    item_category3: itemAttributes.itemCategory3,
    item_category4: itemAttributes.itemCategory4,
    item_category5: itemAttributes.itemCategory5,
    item_list_id: itemListId,
    item_list_name: itemListName,
    price: parseFloat(price),
    value: parseFloat((price - discount).toFixed(2)),
    quantity: 1,
    discount: parseFloat(discount) || 0,
  }
}

// Only the cart and user fields feed the impression payload; `/products/:slug.js` landing
// afterwards replaces the config object without touching them, so this slice stays stable.
const selectTrackingConfig = (config) => (config ? {
  cart: config.cart,
  isCurrentUser: config.isCurrentUser,
  currentUserId: config.currentUserId,
} : undefined)

const useGA4ItemListTracking = ({ products, className }) => {
  const pageConfig = usePageConfig({ select: selectTrackingConfig })
  const itemsPerRow = useItemsPerRow()

  // Determine list type from className
  const { itemListId, itemListName, shouldTrack } = useMemo(() => {
    if (className.includes('similar-items-area')) {
      return { itemListId: 'similar', itemListName: 'similar', shouldTrack: true }
    }
    if (className.includes('also-bought-area')) {
      return { itemListId: 'also_bought', itemListName: 'also_bought', shouldTrack: true }
    }
    if (className.includes('more-items-area')) {
      return { itemListId: 'more_seller_items', itemListName: 'more_seller_items', shouldTrack: true }
    }
    return { itemListId: null, itemListName: null, shouldTrack: false }
  }, [className])

  const totalItems = products.length

  // Use ref to persist buffer across renders
  const bufferRef = useRef(null)
  if (!bufferRef.current) {
    bufferRef.current = new ImpressionsBuffer()
  }

  const buffer = bufferRef.current

  const trackItemImpression = useCallback((product, index) => {
    // Early exit if missing required data
    if (!window.dataLayer || !pageConfig?.cart || !product) {
      return
    }

    // Prepare item data
    const itemData = prepareItemData(product, pageConfig, index, itemListId, itemListName)
    if (!itemData) {
      return
    }

    // Add to buffer (will return false if already tracked)
    const wasAdded = buffer.addImpressionToQueue(itemData)
    if (!wasAdded) {
      return
    }

    // Check if we should fire the event
    // Improvement over legacy: also fires on last item to avoid orphaned items in buffer
    const shouldFire = buffer.getCount() >= itemsPerRow || index === totalItems - 1

    if (shouldFire) {
      const itemsToTrack = buffer.flushImpressions(itemsPerRow)

      if (itemsToTrack.length > 0) {
        if (typeof window.trackViewItemList !== 'function') {
          return
        }

        window.trackViewItemList({
          itemListId,
          itemListName,
          items: itemsToTrack,
          userId: pageConfig?.currentUserId,
          eventSource: 'new_item_page',
        })
      }
    }
  }, [pageConfig, itemListId, itemListName, itemsPerRow, totalItems, buffer])

  // Return null if tracking is not enabled for this list type
  return shouldTrack ? trackItemImpression : null
}

export default useGA4ItemListTracking
